import React, { useEffect, useMemo, useState } from 'react'
import { Grid, makeStyles, Button, Paper, StatusDot, TableRow, Typography, CheckIcon } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { EstimatorAvatar, ResultsTable, useClientOptions, useDialogs, useUsers } from '@paintscout/ui-business'
import type { Payment, QuoteDocument } from 'paintscout'
import { getActivityColor, removeAuth0Prefix } from '@paintscout/util'
import moment from 'moment'
import type { Owner } from '@paintscout/api'
import { formatCurrency } from '@paintscout/util/builder'
import { PaymentsDialog, ViewPaymentDialog } from '@paintscout/react-quote'
import { useQuoteQuery, useSearchActivitiesQuery } from '@paintscout/api'
import AddIcon from '@material-ui/icons/Add'

const useStyles = makeStyles<Theme, DealPaymentsTable>((theme) => ({
  root: {
    border: 'none'
  },
  titleContainer: {
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(1),
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  paymentRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: theme.spacing(2),
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96
    },
    '&:not(:last-child)': {
      borderBottom: `2px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    }
  },
  subtitle: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    paddingLeft: theme.spacing(2)
  },
  paymentHeaderContainer: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'row',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    gap: theme.spacing(1.5)
  },
  paidContainer: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.borderRadius.lg,
    border: `2px solid ${theme.paintscout.palette.semantic.Success.S70}`,
    backgroundColor: theme.paintscout.palette.semantic.Success.S95,
    width: '100%'
  },
  pendingContainer: {
    padding: theme.spacing(2),
    border: `2px solid ${theme.paintscout.palette.semantic.Warning.S80}`,
    backgroundColor: theme.paintscout.palette.semantic.Warning.S95,
    borderRadius: theme.borderRadius.lg,
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  fullyPaidContainer: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: theme.borderRadius.lg,
    border: `2px solid ${theme.paintscout.palette.semantic.Success.S70}`,
    backgroundColor: theme.paintscout.palette.semantic.Success.S95,
    width: '100%'
  },
  fullyPaidCheckContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: theme.borderRadius.full,
    backgroundColor: theme.paintscout.palette.semantic.Success.S70,
    color: theme.palette.common.white
  },
  fullyPaidTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(2)
  },
  paidTitle: {
    color: theme.paintscout.palette.semantic.Success.S60
  }
}))

export interface DealPaymentRow extends Omit<Payment, 'status'> {
  status: 'final' | 'partial' | 'pending'
  owner: Owner & { color?: string }
  quote: Partial<QuoteDocument>
}

export interface DealPaymentsTable {
  relatedDocs: Partial<QuoteDocument>[]
  loading?: boolean
  hideTitle?: boolean
  addClickHandler?: () => void
  onPaymentConfirm?: () => void
}

export default function DealPaymentsTable(props: DealPaymentsTable) {
  const classes = useStyles(props)
  const { loading, relatedDocs: relatedDocsProps, hideTitle, addClickHandler, onPaymentConfirm } = props
  const { userPictures, users } = useUsers()
  const { options } = useClientOptions()
  const { openDialog, dismissDialog } = useDialogs()
  const [relatedDocs, setRelatedDocs] = useState<Partial<Partial<QuoteDocument>>[]>(relatedDocsProps)
  const [relatedInvoice, setRelatedInvoice] = useState<QuoteDocument | null>(null)
  const {
    loading: loadingQuote,
    error,
    refetch
  } = useQuoteQuery({
    skip: !relatedDocsProps.find((doc) => doc.is_invoice),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: { id: relatedDocsProps.find((doc) => doc.is_invoice)?._id },
    onCompleted: (data) => {
      setRelatedInvoice(data.quote as QuoteDocument)
    }
  })

  // Fetch activity owners for payments with activityIds
  const activityIds = useMemo(
    () =>
      Array.from(new Set(relatedDocs.flatMap((doc) => doc.payments?.map((p) => p.activityId).filter(Boolean) || []))),
    [relatedDocs]
  )

  useEffect(() => {
    if (relatedDocsProps.find((doc) => doc.is_invoice) && !relatedInvoice && !loadingQuote && !error) {
      refetch({ id: relatedDocsProps.find((doc) => doc.is_invoice)._id })
    }
  }, [relatedDocs])

  useEffect(() => {
    setRelatedDocs(relatedDocsProps)
    if (!relatedDocsProps.some((doc) => doc.is_invoice)) {
      setRelatedInvoice(null)
    }
  }, [relatedDocsProps])

  const { data: activitiesData } = useSearchActivitiesQuery({
    variables: {
      query: activityIds.map((id) => `id:"${id}" OR _id:"${id}"`).join(' OR '),
      limit: activityIds.length * 2,
      skipSanitize: true
    },
    skip: activityIds.length === 0
  })

  const activityOwnerMap = useMemo(() => {
    const map: Record<string, Owner> = {}
    activitiesData?.searchActivities?.activities?.forEach(
      (a) => a._id && a.details?.owner && (map[a._id] = a.details.owner as Owner)
    )
    return map
  }, [activitiesData])

  function handleViewProcessedPayment({
    activityId,
    row,
    quote
  }: {
    activityId: string
    row?: DealPaymentRow
    quote?: QuoteDocument
  }) {
    openDialog(ViewPaymentDialog, {
      quote: quote || (row.quote as QuoteDocument),
      onDeal: true,
      activityId: activityId,
      onClose: dismissDialog
    })
  }

  const handleRowClick = (row: DealPaymentRow) => {
    const quotePayments = row.quote.payments
    const paymentIndex = quotePayments.findIndex((p) => p.id === row.id)
    if (row.activityId && !row.manual) {
      handleViewProcessedPayment({ activityId: row.activityId, row })
    } else {
      openDialog(PaymentsDialog, {
        quoteId: row.quote._id,
        quote: row.quote as QuoteDocument,
        paymentIndex,
        onDeal: true,
        onConfirm: (quote) => {
          setRelatedDocs((prevDocs) => {
            return prevDocs.map((doc) => (doc._id === quote._id ? quote : doc))
          })
          onPaymentConfirm?.()
          dismissDialog()
        },
        onCancel: dismissDialog
      })
    }
  }

  const rows: DealPaymentRow[] = useMemo(() => {
    return relatedDocs
      .map((doc) => {
        return doc.payments.map((payment) => {
          const balanceDue = doc.totals.balance_due
          const final =
            balanceDue === 0 && doc.payments.length > 0 && payment.id === doc.payments[doc.payments.length - 1].id
          let status: 'final' | 'partial' | 'pending'
          if (final) {
            status = 'final'
          } else if (payment.status === 'pending') {
            status = 'pending'
          } else {
            status = 'partial'
          }

          // Use activity owner if available, otherwise fall back to doc owner
          const owner = (payment.activityId && activityOwnerMap[payment.activityId]) || doc.owner

          // Enhance with user color if available
          const user = owner?.id
            ? users.find((u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(owner.id))
            : null

          return {
            ...payment,
            status,
            owner: { ...owner, color: user?.user_metadata?.color } as Owner & { color?: string },
            quote: doc
          }
        })
      })
      .flat()
  }, [relatedDocs, activityOwnerMap, users])

  const renderRow = (row: DealPaymentRow) => {
    const subtitle = [
      row.amount ? formatCurrency({ options, value: row.amount }) : '',
      moment(row.date).format('MMM D, YYYY')
    ]
      .filter(Boolean)
      .join(' · ')
    return (
      <TableRow
        key={row.id}
        data-testid={`payment-${row.id}`}
        onClick={() => {
          handleRowClick(row)
        }}
      >
        <Grid container className={classes.paymentRow}>
          <Grid item xs style={{ paddingRight: '8px' }}>
            <StatusDot size={9} status={row.status} />
            <Typography variant={'subtitle2'} color="textSecondary" className={classes.subtitle}>
              {subtitle}
            </Typography>
          </Grid>
          {row.owner && (
            <Grid item>
              <EstimatorAvatar
                userPictures={userPictures}
                user={row.owner}
                width="36px"
                height="36px"
                fontSize="0.9rem"
                avatarColor={getActivityColor(row.owner.color)}
              />
            </Grid>
          )}
        </Grid>
      </TableRow>
    )
  }
  const fullyPaid = useMemo(
    () => relatedDocs.every((doc) => !doc.is_invoice || doc.totals.balance_due === 0),
    [relatedDocs]
  )
  const outstandingBalance = useMemo(
    () => relatedDocs.reduce((sum, doc) => (doc.is_invoice ? sum + doc.totals.balance_due : sum), 0),
    [relatedDocs]
  )
  const totalPayments = useMemo(
    () => rows.reduce((sum, row) => sum + (row.status !== 'pending' ? row.amount : 0), 0),
    [rows]
  )

  const hasPayments = totalPayments > 0
  const hasPaymentsOrRequests = rows.length > 0
  const canAddPaymentsToEstimates = options?.options?.quotes?.allowQuotePayments === true
  const hasInvoice = relatedDocsProps.some((doc) => doc.is_invoice === true)
  const noResultsMessage =
    !canAddPaymentsToEstimates && !hasInvoice ? 'Deal requires an invoice to add payments.' : 'No payments found.'

  return (
    <Paper className={classes.root}>
      {!hideTitle && (
        <div className={classes.titleContainer}>
          <Typography variant="h3">{'Payments'}</Typography>
          {!fullyPaid && relatedInvoice && addClickHandler && (
            <Button variant="hoverUnderline" color="primary" startIcon={<AddIcon />} onClick={() => addClickHandler()}>
              {'Add'}
            </Button>
          )}
        </div>
      )}
      <div>
        {fullyPaid && hasPayments ? (
          <div className={classes.paymentHeaderContainer}>
            <div className={classes.fullyPaidContainer}>
              <div className={classes.fullyPaidCheckContainer}>
                <CheckIcon />
              </div>
              <div className={classes.fullyPaidTextContainer}>
                <Typography variant="h4" className={classes.paidTitle} weight="bold">
                  {'Fully Paid!'}
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                >{`${formatCurrency({ options, value: totalPayments })}`}</Typography>
              </div>
            </div>
          </div>
        ) : hasPaymentsOrRequests ? (
          <div className={classes.paymentHeaderContainer}>
            <div className={classes.pendingContainer}>
              <Typography variant="subtitle1" color="textSecondary">{`Outstanding`}</Typography>
              <Typography variant="h4">{`${formatCurrency({ options, value: outstandingBalance })}`}</Typography>
            </div>
            <div className={classes.paidContainer}>
              <Typography variant="subtitle1" color="textSecondary">{`Total Paid`}</Typography>
              <Typography variant="h4">{`${formatCurrency({ options, value: totalPayments })}`}</Typography>
            </div>
          </div>
        ) : null}
      </div>
      <ResultsTable
        noResultsMessage={noResultsMessage}
        columns={<></>}
        renderRow={renderRow}
        rows={rows}
        rowsPerPage={rows.length}
        loading={loading}
        disablePagination
      />
    </Paper>
  )
}
